// Lightweight toast notifications. Replaces native alert() for non-blocking feedback.
// Usage:
//   import { toast } from '$lib/stores/toast.svelte';
//   toast.success('Séance enregistrée');
//   toast.error('La séance n\'a pas pu être enregistrée. Réessayez.');
//   toast.info('Génération en cours...');
//   toast.success('Séance supprimée.', 10000, { label: 'Annuler', run: restore });

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastAction {
	label: string;
	run: () => void | Promise<void>;
}

export interface ToastItem {
	id: number;
	variant: ToastVariant;
	message: string;
	timeout: number;
	action?: ToastAction;
}

let nextId = 1;
let _items = $state<ToastItem[]>([]);

function push(variant: ToastVariant, message: string, timeout?: number, action?: ToastAction) {
	const id = nextId++;
	// Un toast porteur d'action laisse le temps de cliquer.
	const ttl = timeout ?? (action ? 10000 : variant === 'error' ? 8000 : 5000);
	_items = [..._items, { id, variant, message, timeout: ttl, action }];
	if (ttl > 0) {
		setTimeout(() => dismiss(id), ttl);
	}
	return id;
}

function dismiss(id: number) {
	_items = _items.filter((t) => t.id !== id);
}

function clear() {
	_items = [];
}

export const toast = {
	get items(): ToastItem[] {
		return _items;
	},
	success(message: string, timeout?: number, action?: ToastAction) {
		return push('success', message, timeout, action);
	},
	error(message: string, timeout?: number, action?: ToastAction) {
		return push('error', message, timeout, action);
	},
	info(message: string, timeout?: number, action?: ToastAction) {
		return push('info', message, timeout, action);
	},
	dismiss,
	clear
};
